/**
 * Order helpers that are NOT about rendering a status badge.
 *
 * Status labels and status colours belong to `@/components/ui/status-badge`:
 * `<StatusBadge kind="order|financial|invoice|payment" status={…} />` owns both
 * the word and the tone, so the same state cannot read differently on two
 * screens.
 */
import { SERVICE_CLIENT_LOCATION } from "@/lib/locationUtils";

/** Catalog name of the delivery line added to delivered orders. */
export const DELIVERY_PRODUCT_NAME = "Livraison";

export function isDeliveryItem(item: { product_name: string }): boolean {
  return item.product_name === DELIVERY_PRODUCT_NAME;
}

export function getDisplayLocationName(name?: string | null): string {
  if (!name) return "Non assignée";
  if (name === SERVICE_CLIENT_LOCATION) return "À compléter";
  return name;
}

/**
 * Get display name for a contact, with fallback to account's main contact
 * @param contact - The selected contact (from order.payer_contact or order.recipient_contact)
 * @param account - The account object with main_contact and contacts_count
 * @returns The contact's full name, or "-" if not available
 */
export function getContactDisplayName(
  contact?: { full_name?: string; first_name?: string; last_name?: string } | null,
  account?: {
    contacts_count?: number;
    main_contact?: { full_name?: string; first_name?: string; last_name?: string } | null;
  } | null,
): string {
  if (contact?.full_name) return contact.full_name;
  const fallback =
    account?.main_contact?.full_name ||
    [account?.main_contact?.first_name, account?.main_contact?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
  if (account?.contacts_count === 1 && fallback) return fallback;
  return "-";
}

export function getPaymentStatusBgColor(status: string): string {
  switch (status) {
    case "completed":
      return "bg-green-50 border-green-200";
    case "pending":
      return "bg-yellow-50 border-yellow-200";
    case "failed":
    case "cancelled":
      return "bg-red-50 border-red-200";
    default:
      return "bg-gray-50 border-gray-200";
  }
}

export function getPaymentStatusDotColor(status: string): string {
  switch (status) {
    case "completed":
      return "bg-green-500";
    case "pending":
      return "bg-yellow-500";
    case "failed":
    case "cancelled":
      return "bg-red-500";
    default:
      return "bg-gray-500";
  }
}

// Payment method display functions
export function getPaymentMethodLabel(method: string): string {
  const methodMap: Record<string, string> = {
    cash: "Espèces",
    card: "Carte",
    website: "Site",
    link: "Lien",
    invoice: "Facture",
  };
  return methodMap[method] || method;
}

// Delivery time formatting utility
export interface FormatDeliveryTimeOptions {
  mode: "planned" | "actual" | "failure";
  delivery_date?: string | null;
  delivery_time?: string | null;
  delivery_time_slot?: string | null;
  actual_timestamp?: string | null;
  failure_timestamp?: string | null;
}

export interface FormattedDeliveryTime {
  date: string;
  time: string;
  full: string;
}

export function formatDeliveryTime(
  options: FormatDeliveryTimeOptions
): FormattedDeliveryTime {
  const {
    mode,
    delivery_date,
    delivery_time,
    delivery_time_slot,
    actual_timestamp,
    failure_timestamp,
  } = options;

  // Handle failure mode - use failure_timestamp
  if (mode === "failure" && failure_timestamp) {
    const date = new Date(failure_timestamp);
    if (!isNaN(date.getTime())) {
      const dateStr = date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
      });
      const timeStr = date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return {
        date: dateStr,
        time: timeStr,
        full: `${dateStr} à ${timeStr}`,
      };
    }
  }

  // Handle actual mode - use explicit completion timestamp first
  if (mode === "actual" && (actual_timestamp || delivery_time)) {
    const date = new Date(actual_timestamp || delivery_time || "");
    if (!isNaN(date.getTime())) {
      const dateStr = date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
      });
      const timeStr = date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return {
        date: dateStr,
        time: timeStr,
        full: `${dateStr} à ${timeStr}`,
      };
    }
  }

  // Handle planned mode - use delivery_date + delivery_time_slot
  if (mode === "planned" && delivery_date) {
    const date = new Date(delivery_date);
    if (!isNaN(date.getTime())) {
      const dateStr = date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
      });

      let timeStr = "";
      if (delivery_time_slot) {
        timeStr = delivery_time_slot;
      } else if (delivery_time) {
        // Fallback to delivery_time if slot not available
        const time = new Date(`2000-01-01T${delivery_time}`);
        if (!isNaN(time.getTime())) {
          timeStr = time.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      }

      return {
        date: dateStr,
        time: timeStr,
        full: timeStr ? `${dateStr} à ${timeStr}` : dateStr,
      };
    }
  }

  // Fallback for any mode if no date available
  return { date: "", time: "", full: "Date non définie" };
}
